// pages/Solutions/SolutionDetail.js
import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './SolutionDetail.css';

const SolutionDetail = () => {
  const { slug } = useParams();
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Validation check
  const validSlugs = [
    'field-service-optimization',
    'operational-forecasting',
    'ops-analytics-anomaly-detection',
    'knowledge-intelligence'
  ];

  if (!validSlugs.includes(slug)) {
    return (
      <div className="solution-detail-wrap" style={{ textAlign: 'center', padding: '10rem 2rem' }}>
        <h2>Solution Not Found</h2>
        <p>The solution you are looking for does not exist.</p>
        <Link to="/" className="btn" style={{ marginTop: '2rem' }}>Back to Home</Link>
      </div>
    );
  }

  // Fetch lists from translations
  const challengesData = t(`solutionsDetails.${slug}.challenges`, { returnObjects: true });
  const challenges = Array.isArray(challengesData) ? challengesData : [];

  const featuresData = t(`solutionsDetails.${slug}.features`, { returnObjects: true });
  const features = Array.isArray(featuresData) ? featuresData : [];

  const benefitsData = t(`solutionsDetails.${slug}.benefits`, { returnObjects: true });
  const benefits = Array.isArray(benefitsData) ? benefitsData : [];

  // Dynamic visual charts based on slug
  const renderVisualMock = () => {
    switch (slug) {
      case 'field-service-optimization':
        return (
          <svg width="100%" height="220" viewBox="0 0 300 220" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="300" height="220" rx="8" fill="#161616" stroke="#2a2a2a" strokeWidth="1.5"/>
            <circle cx="80" cy="60" r="8" fill="#4d4dff"/>
            <circle cx="150" cy="150" r="8" fill="#ffffff" stroke="#4d4dff" strokeWidth="2"/>
            <circle cx="220" cy="80" r="8" fill="#4d4dff"/>
            <path d="M88 65L142 145M212 85L158 145" stroke="#ffffff" strokeWidth="1.5" strokeDasharray="3 3"/>
            <text x="20" y="200" fill="#808080" fontSize="10" fontFamily="monospace">DYNAMIC DISPATCH MATCHING</text>
          </svg>
        );
      case 'operational-forecasting':
        return (
          <svg width="100%" height="220" viewBox="0 0 300 220" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="300" height="220" rx="8" fill="#161616" stroke="#2a2a2a" strokeWidth="1.5"/>
            <path d="M40 160C80 160 100 60 140 60C180 60 200 120 260 120" stroke="#ffffff" strokeWidth="2"/>
            <path d="M40 160C80 160 100 60 140 60C180 60 200 120 260 120" stroke="#4d4dff" strokeWidth="6" strokeOpacity="0.15"/>
            <line x1="40" y1="180" x2="260" y2="180" stroke="#2a2a2a" strokeWidth="1.5"/>
            <line x1="40" y1="40" x2="40" y2="180" stroke="#2a2a2a" strokeWidth="1.5"/>
            <text x="20" y="200" fill="#808080" fontSize="10" fontFamily="monospace">FORECAST DEMAND INTENSITY</text>
          </svg>
        );
      case 'ops-analytics-anomaly-detection':
        return (
          <svg width="100%" height="220" viewBox="0 0 300 220" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="300" height="220" rx="8" fill="#161616" stroke="#2a2a2a" strokeWidth="1.5"/>
            <circle cx="90" cy="110" r="5" fill="#808080"/>
            <circle cx="120" cy="130" r="5" fill="#808080"/>
            <circle cx="150" cy="90" r="5" fill="#808080"/>
            <circle cx="180" cy="40" r="7" fill="#ffffff" stroke="#ff4d4d" strokeWidth="2"/>
            <circle cx="210" cy="120" r="5" fill="#808080"/>
            <text x="175" y="28" fill="#ff4d4d" fontSize="9" fontFamily="sans-serif" fontWeight="bold">ANOMALY</text>
            <text x="20" y="200" fill="#808080" fontSize="10" fontFamily="monospace">LATENCY DETECTION OUTLIER</text>
          </svg>
        );
      case 'knowledge-intelligence':
        return (
          <svg width="100%" height="220" viewBox="0 0 300 220" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="300" height="220" rx="8" fill="#161616" stroke="#2a2a2a" strokeWidth="1.5"/>
            <rect x="20" y="30" width="160" height="36" rx="6" fill="#242424"/>
            <text x="32" y="52" fill="#ffffff" fontSize="9" fontFamily="sans-serif">Technician: How to fix E02 error?</text>
            <rect x="100" y="80" width="180" height="46" rx="6" fill="#4d4dff" fillOpacity="0.2" stroke="#4d4dff" strokeWidth="1"/>
            <text x="112" y="98" fill="#ffffff" fontSize="9" fontFamily="sans-serif">Conveks AI: E02 is a pressure fault.</text>
            <text x="112" y="114" fill="#808080" fontSize="8" fontFamily="sans-serif">Check bypass valve B-12 and restart.</text>
            <text x="20" y="200" fill="#808080" fontSize="10" fontFamily="monospace">SEMANTIC RUNBOOK ASSISTANT</text>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="solution-detail-wrap">
      {/* Product Hero */}
      <div className="solution-hero">
        <h1>{t(`solutionsDetails.${slug}.title`)}</h1>
        <p className="solution-hero-desc">
          {t(`solutionsDetails.${slug}.solution`)}
        </p>
      </div>

      {/* Split Grid: Problem vs Challenges */}
      <div className="solution-grid-split">
        <div className="solution-card-left">
          <h2>{t(`solutionsDetails.${slug}.problemTitle`)}</h2>
          <p>{t(`solutionsDetails.${slug}.problem`)}</p>
        </div>

        <div className="solution-card-right">
          <h2>{t(`solutionsDetails.${slug}.challengesTitle`)}</h2>
          <ul className="challenge-list">
            {challenges.map((challenge, idx) => (
              <li key={idx}>{challenge}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Conveks Solution Showcase Banner */}
      <div className="conveks-solution-section">
        <div className="conveks-solution-inner">
          <h2>{t(`solutionsDetails.${slug}.solutionTitle`)}</h2>
          <p>{t(`solutionsDetails.${slug}.solution`)}</p>
        </div>
      </div>

      {/* Product Features Detail */}
      <div className="detail-features-section">
        <h2>{t(`solutionsDetails.${slug}.featuresTitle`)}</h2>
        <div className="detail-features-grid">
          {features.map((feature, idx) => (
            <div className="detail-feature-card" key={idx}>
              <h3>{feature.name}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Business Benefits + Visual Diagram Row */}
      <div className="detail-benefits-section">
        <div className="detail-benefits-text">
          <h2>{t(`solutionsDetails.${slug}.benefitsTitle`)}</h2>
          <ul className="benefits-bullet-list">
            {benefits.map((benefit, idx) => (
              <li key={idx}>{benefit}</li>
            ))}
          </ul>
        </div>
        <div className="detail-benefits-visual">
          {renderVisualMock()}
        </div>
      </div>

      {/* CTA Section */}
      <div className="solution-cta-banner">
        <div className="solution-cta-card">
          <h2>{t('solutionCTA.title')}</h2>
          <p>{t('solutionCTA.description')}</p>
          <Link to="/#contact" className="btn">
            {t('navbar.contact')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SolutionDetail;
